import React from "react";
import { debounce } from "@/utils/debounce";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  type ColumnDef,
  type ColumnFiltersState,
  type Row,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type FilterFn,
} from "@tanstack/react-table";
import {
  ChevronDown,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  Clock,
  GripVerticalIcon,
  Info,
  Search,
  Sliders,
  Zap,
  Edit2,
  Check,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Sheet, SheetTrigger } from "@/components/ui/sheet";
import { ProductSheet } from "@/components";
import { Category, ProductVariant, QuickReorder } from "@/types/AppTypes";
import { DropdownMenuItem } from "@radix-ui/react-dropdown-menu";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { addToCart, addToCartBulk } from "@/store/slices/appSlice";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../ui/form";
import { ScrollArea } from "../ui/scroll-area";
import capitalizeWords from "@/utils/helper/capitalizeWords";
import { cn } from "@/lib/utils";

type QuickReorderCompoProps = {
  products: QuickReorder[];
  categories: Category[];
  handleStockUpdate?: (product: QuickReorder, stock: number) => void;
  stockLoading?: boolean;
  handleReorderProduct?: (product: QuickReorder, quantity: number) => void;
  dialogOpen: boolean;
  setDialogOpen: (arg0: boolean) => void;
  reorderLoading?: boolean;
  className?: string;
  onSearchChange?: (search: string) => void;
  children?: React.ReactNode;
};

const reorderQuantityForm = z.object({
  quantity: z
    .string()
    .min(1, "Quantity is required")
    .regex(/^\d+$/, "Quantity must be a number"),
});

function DragHandle({ id }: { id: string }) {
  const { attributes, listeners } = useSortable({ id });
  return (
    <Button
      {...attributes}
      {...listeners}
      variant="ghost"
      size="icon"
      className="size-7 text-muted-foreground hover:bg-transparent"
    >
      <GripVerticalIcon className="size-3 text-muted-foreground" />
      <span className="sr-only">Drag to reorder</span>
    </Button>
  );
}

declare module "@tanstack/react-table" {
  interface FilterFns {
    categoryFilter: FilterFn<any>;
  }
}

const QuickReorderCompo: React.FC<QuickReorderCompoProps> = React.memo(
  ({
    products: productsProp,
    categories,
    handleStockUpdate,
    stockLoading,
    handleReorderProduct,
    dialogOpen,
    setDialogOpen,
    reorderLoading,
    className,
    onSearchChange,
    children,
  }) => {
    const [products, setProducts] =
      React.useState<QuickReorder[]>(productsProp);
    const { categoriesMap, brandsMap } = useAppSelector((state) => state.app);

    const dispatch = useAppDispatch();

    React.useEffect(() => {
      setProducts(productsProp);
    }, [productsProp]);

    const categoriesWithAll = React.useMemo(
      () => [{ name: "all", label: "All Categories" }, ...(categories || [])],
      [categories]
    );

    const [inputValue, setInputValue] = React.useState("");
    const [searchQuery, setSearchQuery] = React.useState("");
    const deferredSearchQuery = React.useDeferredValue(searchQuery);
    const [rowSelection, setRowSelection] = React.useState({});
    const [columnVisibility, setColumnVisibility] =
      React.useState<VisibilityState>({});
    const [columnFilters, setColumnFilters] =
      React.useState<ColumnFiltersState>([]);
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [pagination, setPagination] = React.useState({
      pageIndex: 0,
      pageSize: 10,
    });

    const [selectedCategories, setSelectedCategories] = React.useState<
      Record<string, boolean>
    >({
      all: true,
    });

    const [editingStockId, setEditingStockId] = React.useState<string | null>(
      null
    );
    const [stockInput, setStockInput] = React.useState<string>("");
    // const deferredStockInput = useDeferredValue(stockInput);
    const [reorderProduct, setReorderProduct] =
      React.useState<QuickReorder | null>(null);

    const form = useForm<z.infer<typeof reorderQuantityForm>>({
      resolver: zodResolver(reorderQuantityForm),
      defaultValues: {
        quantity: "",
      },
    });

    const handleSubmit = () => {
      const data = form.getValues();
      if (parseInt(data.quantity || "0", 10) < 1) {
        form.setError("quantity", {
          type: "manual",
          message: "Quantity must be at least 1",
        });
        return;
      }
      if (handleReorderProduct) {
        handleReorderProduct(
          reorderProduct as QuickReorder,
          parseInt(data.quantity, 10)
        );
      }
    };

    const handleCategoryChange = (categoryName: string) => {
      setSelectedCategories((prev) => {
        const newSelection = { ...prev };
        newSelection[categoryName] = !prev[categoryName];
        if (categoryName === "all" && newSelection["all"]) {
          Object.keys(newSelection).forEach((key) => {
            if (key !== "all") newSelection[key] = false;
          });
        }
        if (categoryName !== "all" && newSelection[categoryName]) {
          newSelection["all"] = false;
        }
        return newSelection;
      });
    };

    React.useEffect(() => {
      const activeCategories = Object.keys(selectedCategories).filter(
        (cat) => selectedCategories[cat] && cat !== "all"
      );
      if (activeCategories.length > 0) {
        setColumnFilters((prev) => {
          const otherFilters = prev.filter(
            (filter) => filter.id !== "category"
          );
          return [
            ...otherFilters,
            {
              id: "category",
              value: activeCategories,
              filterFn: "categoryFilter",
            },
          ];
        });
      } else {
        setColumnFilters((prev) =>
          prev.filter((filter) => filter.id !== "category")
        );
      }
    }, [selectedCategories]);

    const columns: ColumnDef<any>[] = [
      {
        id: "drag",
        header: () => null,
        cell: ({ row }) => <DragHandle id={row.original.id} />,
      },
      {
        id: "select",
        header: ({ table }) => (
          <div className="flex items-center justify-center">
            <Checkbox
              checked={
                table.getIsAllPageRowsSelected() ||
                (table.getIsSomePageRowsSelected() && "indeterminate")
              }
              className="data-[state=checked]:bg-black data-[state=checked]:text-white"
              onCheckedChange={(value) =>
                table.toggleAllPageRowsSelected(!!value)
              }
              aria-label="Select all"
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex items-center justify-center">
            <Checkbox
              className="data-[state=checked]:bg-black data-[state=checked]:text-white"
              checked={row.getIsSelected()}
              onCheckedChange={(value) => row.toggleSelected(!!value)}
              aria-label="Select row"
            />
          </div>
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: "image",
        header: () => <div className="pl-3">Image</div>,
        cell: ({ row }) => {
          const variant = row.original?.variant
            ? (JSON.parse(row.original?.variant) as ProductVariant)
            : null;

          return (
            <div className="relative w-16 h-12  overflow-hidden rounded-md  ml-2">
              <img
                src={variant?.images?.[0] || "/placeholder.webp"}
                alt={variant?.name}
                className="absolute inset-0 w-full h-full object-fill"
              />
            </div>
          );
        },
      },
      {
        id: "product",
        accessorKey: "name",
        header: "Product",
        cell: ({ row }) => {
          const variant = row.original?.variant
            ? (JSON.parse(row.original?.variant) as ProductVariant)
            : null;
          return (
            <div>
              <div className="flex items-center gap-1">
                <span className="font-medium">
                  {capitalizeWords(variant?.name || "")}
                </span>
                <Sheet>
                  <SheetTrigger asChild>
                    <Button
                      className="hover:bg-black hover:text-white bg-transparent text-gray-600"
                      size="icon"
                    >
                      <Info className="h-4 w-4 " />
                    </Button>
                  </SheetTrigger>
                  {/* <ProductSheet productDetails={variant ?? {}} /> */}
                </Sheet>
              </div>
              {row.original?.quantity && (
                <span className="text-sm text-muted-foreground">
                  {row.original?.quantity}
                </span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "stock",
        header: () => <div className="w-36 text-center ">Stock</div>,
        cell: ({ row }) => {
          const product = row.original;
          const isEditing = editingStockId === product.id;
          return stockLoading ? (
            <>updating...</>
          ) : (
            <div className="flex items-center justify-center gap-2 px-4 text-center">
              {isEditing ? (
                <>
                  <Input
                    type="number"
                    className="w-28 border rounded px-1 py-0.5 text-center h-8 "
                    value={stockInput}
                    onChange={(e) => setStockInput(e.target.value)}
                    autoFocus
                  />
                  <Button
                    className="text-green-600 bg-transparent"
                    onClick={() => handleConfirmEdit(product)}
                    title="Confirm"
                  >
                    <Check size={16} />
                  </Button>
                  <Button
                    className="text-red-600 bg-transparent"
                    onClick={handleCancelEdit}
                    title="Cancel"
                  >
                    <X size={16} />
                  </Button>
                </>
              ) : (
                <div className="pl-2 flex items-center justify-center">
                  <div className="text-center text-black">
                    {product.stock || "5"}
                  </div>
                  <Button
                    className="ml-1 text-gray-500 hover:text-black bg-transparent"
                    onClick={() => handleEditStock(product)}
                    title="Edit"
                  >
                    <Edit2 size={16} />
                  </Button>
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "brand",
        header: "Brand",
        cell: ({ row }) => {
          const brandId = row.original?.brand_id;

          const brandName = brandId && brandsMap?.[brandId]?.name;

          return <>{capitalizeWords(brandName || "N/A")}</>;
        },
      },
      {
        accessorKey: "category",
        header: "Category",
        filterFn: "categoryFilter",
        cell: ({ row }) => {
          const categories = row.original?.category_id;
          if (Array.isArray(categories) && categories.length > 0) {
            return (
              <>
                {categories
                  .map((catId: string) =>
                    capitalizeWords(categoriesMap?.[catId]?.name || "")
                  )
                  .filter(Boolean)
                  .join(", ") || "N/A"}
              </>
            );
          }
          return <>N/A</>;
        },
      },
      {
        accessorKey: "price",
        header: () => <div className="text-right">Price</div>,
        cell: ({ row }) => {
          const variant = row.original?.variant
            ? (JSON.parse(row.original?.variant) as ProductVariant)
            : null;

          return (
            <div className="text-right">${variant?.price?.toFixed(2)}</div>
          );
        },
      },
      {
        accessorKey: "lastOrdered",
        header: () => <div className="text-center px-2">Last Ordered</div>,
        cell: ({ row }) => {
          const lastOrder = row.original?.last_order;
          const date = new Date(lastOrder);
          const pretty = date.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });

          return (
            <div className="flex items-center justify-center gap-2">
              <Clock className="size-3.5 text-gray-600" />
              <span>{pretty ?? "Not yet"}</span>
            </div>
          );
        },
      },
      {
        id: "actions",
        cell: ({ row }) => (
          <Button
            className="hover:bg-black hover:text-white bg-transparent text-gray-800"
            type="button"
            onClick={() => {
              setReorderProduct(row.original);
              setDialogOpen(true);
              form.reset({ quantity: "" });
            }}
          >
            <Zap className="h-4 w-4" />
            Reorder
          </Button>
        ),
      },
    ];

    React.useEffect(() => {
      if (deferredSearchQuery) {
        setColumnFilters((prev) => {
          const categoryFilter = prev.find(
            (filter) => filter.id === "category"
          );
          const filters = categoryFilter ? [categoryFilter] : [];
          return [
            ...filters,
            {
              id: "product",
              value: deferredSearchQuery,
            },
          ];
        });
      } else {
        setColumnFilters((prev) =>
          prev.filter((filter) => filter.id !== "product")
        );
      }
    }, [deferredSearchQuery]);

    const sortableId = React.useId();
    const sensors = useSensors(
      useSensor(MouseSensor, {}),
      useSensor(TouchSensor, {}),
      useSensor(KeyboardSensor, {})
    );

    const dataIds = React.useMemo<UniqueIdentifier[]>(
      () => products?.map(({ id }) => id) || [],
      [products]
    );

    const table = useReactTable({
      data: products,
      columns,
      state: {
        sorting,
        columnVisibility,
        rowSelection,
        columnFilters,
        pagination,
      },
      filterFns: {
        categoryFilter: (row, columnId, filterValues) => {
          if (!filterValues.length) return true;

          const categoryIds = row.original?.category_id;
          if (Array.isArray(categoryIds) && categoryIds.length > 0) {
            const categoryNames = categoryIds
              .map((catId: string) => categoriesMap?.[catId]?.name)
              .filter((name): name is string => Boolean(name));

            return categoryNames.some((categoryName: string) =>
              filterValues.includes(categoryName)
            );
          }
          return false;
        },
      },
      getRowId: (row) => row.id,
      enableRowSelection: true,
      onRowSelectionChange: setRowSelection,
      onSortingChange: setSorting,
      onColumnFiltersChange: setColumnFilters,
      onColumnVisibilityChange: setColumnVisibility,
      onPaginationChange: setPagination,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFacetedRowModel: getFacetedRowModel(),
      getFacetedUniqueValues: getFacetedUniqueValues(),
    });

    function handleDragEnd(event: DragEndEvent) {
      const { active, over } = event;
      if (active && over && active.id !== over.id) {
        setProducts((products) => {
          const oldIndex = dataIds.indexOf(active.id);
          const newIndex = dataIds.indexOf(over.id);
          return arrayMove(products, oldIndex, newIndex);
        });
      }
    }

    function DraggableRow({ row }: { row: Row<ProductVariant> }) {
      const { transform, transition, setNodeRef, isDragging } = useSortable({
        id: row.original.id,
      });
      return (
        <TableRow
          data-state={row.getIsSelected() && "selected"}
          data-dragging={isDragging}
          ref={setNodeRef}
          className="relative z-0 data-[dragging=true]:z-10 data-[dragging=true]:opacity-80"
          style={{
            transform: CSS.Transform.toString(transform),
            transition: transition,
          }}
        >
          {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          ))}
        </TableRow>
      );
    }

    const debouncedSetSearch = React.useMemo(
      () =>
        debounce((value: string) => {
          setSearchQuery(value);
        }, 200),
      []
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);
      debouncedSetSearch(value);
      if (typeof onSearchChange === "function") {
        onSearchChange(value);
      }
    };

    const handleAddSelectedToCart = () => {
      const selectedProductIds = Object.keys(rowSelection);
      const selectedProducts = products.filter((product) =>
        selectedProductIds.includes(product.id)
      );
      if (selectedProducts.length > 0) {
        dispatch(addToCartBulk(selectedProducts));
        setRowSelection({});
      }
    };

    const handleEditStock = (product: QuickReorder) => {
      setEditingStockId(product?.id);
      setStockInput(product?.stock?.toString() || "");
    };

    const handleCancelEdit = () => {
      setEditingStockId(null);
      setStockInput("");
    };

    const handleConfirmEdit = (product: ProductVariant) => {
      if (handleStockUpdate) {
        handleStockUpdate(product, parseInt(stockInput, 10));
      }
      setEditingStockId(null);
      setStockInput("");
    };

    return (
      <div className={cn("container mx-auto py-24 px-4 max-w-7xl", className)}>
        <div className="flex items-center gap-2 mb-2">
          <Zap className="h-6 w-6" />
          <h1 className="text-2xl font-bold">Quick Reorder</h1>
        </div>
        <p className="text-muted-foreground mb-6">
          Easily select multiple products to add to your cart. Drag items to
          reorder them.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search products..."
              className="pl-10 focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 focus:border-none bg-white"
              // value={inputValue}
              // onChange={handleInputChange}
            />
          </div>
          <div className="flex gap-2 md:flex-row flex-col">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2">
                  <Sliders className="h-4 w-4" />
                  Filter
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white py-4 pr-16 pl-2">
                <DropdownMenuItem className="text-md hover:border-none hover:outline-none pl-2 font-medium pb-2">
                  Filter by Category
                </DropdownMenuItem>

                <DropdownMenuCheckboxItem
                  className="hover:border-none hover:outline-none focus:bg-white hover:bg-white cursor-pointer"
                  checked={selectedCategories["all"] || false}
                  onCheckedChange={() => handleCategoryChange("all")}
                >
                  All Categories
                </DropdownMenuCheckboxItem>
                <ScrollArea className="max-h-52">
                  {categoriesWithAll
                    .filter((category) => category.name !== "all")
                    .map((category: Category) => (
                      <DropdownMenuCheckboxItem
                        className="hover:border-none hover:outline-none focus:bg-white hover:bg-white cursor-pointer "
                        key={category.name}
                        checked={selectedCategories[category?.name ?? ""]}
                        onCheckedChange={() =>
                          handleCategoryChange(category?.name ?? "")
                        }
                      >
                        {category?.name}
                      </DropdownMenuCheckboxItem>
                    ))}
                </ScrollArea>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              // onClick={handleAddSelectedToCart}
              className="whitespace-nowrap bg-black hover:bg-gray-800 text-white"
              disabled={Object.keys(rowSelection).length === 0}
            >
              Add Selected to Cart
              <span className="w-5">({Object.keys(rowSelection).length})</span>
            </Button>
          </div>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <DndContext
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
            sensors={sensors}
            id={sortableId}
          >
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-muted">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id} colSpan={header.colSpan}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  <SortableContext
                    items={dataIds}
                    strategy={verticalListSortingStrategy}
                  >
                    {table.getRowModel().rows.map((row) => (
                      <DraggableRow key={row.id} row={row} />
                    ))}
                  </SortableContext>
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </DndContext>
        </div>

        <div className="flex items-center justify-between px-4 mt-4">
          <div className="hidden flex-1 text-sm text-muted-foreground lg:flex">
            {table.getFilteredSelectedRowModel().rows.length} of{" "}
            {table.getFilteredRowModel().rows.length} row(s) selected.
          </div>
          <div className="flex w-full items-center gap-8 lg:w-fit">
            <div className="hidden items-center gap-2 lg:flex">
              <Label htmlFor="rows-per-page" className="text-sm font-medium">
                Rows per page
              </Label>
              <Select
                value={`${table.getState().pagination.pageSize}`}
                onValueChange={(value) => {
                  table.setPageSize(Number(value));
                }}
              >
                <SelectTrigger className="w-20" id="rows-per-page">
                  <SelectValue
                    placeholder={table.getState().pagination.pageSize}
                  />
                </SelectTrigger>
                <SelectContent side="top" className="bg-white">
                  {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                    <SelectItem key={pageSize} value={`${pageSize}`}>
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
           
            <div className="ml-auto flex items-center gap-2 lg:ml-0">
           {children}
            </div>
          </div>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <Form {...form}>
            <form className="space-y-8">
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Enter Quantity</DialogTitle>
                </DialogHeader>
                <DialogDescription>
                  {typeof reorderProduct?.variant === "string"
                    ? JSON.parse(reorderProduct.variant)?.name
                    : ""}
                </DialogDescription>
                <FormField
                  control={form.control}
                  name="quantity"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="quantity"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage>
                        {form.formState.errors.quantity?.message}
                      </FormMessage>
                    </FormItem>
                  )}
                />
                <DialogFooter>
                  <DialogClose asChild>
                    <Button
                      variant="outline"
                      type="button"
                      onClick={() => setDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                  </DialogClose>
                  <Button disabled={reorderLoading} onClick={handleSubmit}>
                    {reorderLoading ? <>Reordering...</> : <>Proceed</>}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </form>
          </Form>
        </Dialog>
      </div>
    );
  }
);

export default QuickReorderCompo;
